/**
 * Authentication Hook for Web Application
 *
 * Provides a comprehensive React hook for authentication functionality
 * that integrates with the shared auth service and store.
 *
 * Features:
 * - Login, logout, and registration
 * - User data access and management
 * - Loading states and error handling
 * - Token management and refresh
 * - Form submission helpers
 */

import { useCallback, useDeferredValue } from 'react';

import { authService } from '@personal-trainer-app/shared';

import type {
  AuthToken,
  DeleteAccountRequest,
  LoginInput,
  LoginOutcome,
  RegisterInput,
  WaitlistInput,
  User,
  TokenValidationResult,
  TwoFactorProof,
} from '@personal-trainer-app/shared';

import { useAuthStore } from '@/stores';

// =============================================================================
// HOOK OPTIONS
// =============================================================================

export interface UseAuthOptions {
  /**
   * Whether to defer loading state changes to prevent suspension errors
   * during navigation. Use this for components that render after navigation
   * to prevent "component suspended while responding to synchronous input" errors.
   */
  deferred?: boolean;
}

// =============================================================================
// HOOK INTERFACE
// =============================================================================

export interface UseAuthReturn {
  // Authentication state
  isAuthenticated: boolean;
  user: User | null;
  token: AuthToken | null;
  refreshToken: string | null;
  tokenExpiry: number | null;

  // Loading states
  isLoading: boolean;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  isRegistering: boolean;
  isRefreshingToken: boolean;

  // Error states
  error: string | null;
  loginError: string | null;
  registerError: string | null;
  refreshError: string | null;

  // Authentication actions
  login: (credentials: LoginInput) => Promise<LoginOutcome>;
  /** Finish a sign-in `login` left waiting on a two-factor code. */
  completeTwoFactorSignIn: (proof: TwoFactorProof) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: (proof: DeleteAccountRequest) => Promise<void>;
  validateInvitationToken: (token: string) => Promise<TokenValidationResult>;
  register: (data: RegisterInput) => Promise<void>;
  waitlist: (data: WaitlistInput) => Promise<void>;
  fetchCurrentUser: () => Promise<void>;
  refreshTokenAction: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (data: {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
  }) => Promise<void>;

  // User management
  updateUser: (updates: Partial<User>) => void;
  clearUser: () => void;

  // Error management
  clearErrors: () => void;
  clearError: (type?: 'login' | 'register' | 'refresh' | 'general') => void;

  // Session management
  checkSessionValidity: () => boolean;
  updateLastActivity: () => void;

  // Auto-refresh management
  autoRefreshEnabled: boolean;
  enableAutoRefresh: () => void;
  disableAutoRefresh: () => void;
  setRefreshThreshold: (threshold: number) => void;

  // Store management
  resetAuthStore: () => void;
}

// =============================================================================
// MAIN HOOK
// =============================================================================

export const useAuth = (options: UseAuthOptions = {}): UseAuthReturn => {
  const { deferred = false } = options;
  const authStore = useAuthStore();
  const {
    // Authentication state
    isAuthenticated,
    user,
    token,
    refreshToken: storeRefreshToken,
    tokenExpiry,

    // Loading states
    isLoggingIn,
    isLoggingOut,
    isRegistering,
    isRefreshingToken,

    // Error states
    error,
    loginError,
    registerError,
    refreshError,

    // Store actions
    login: storeLogin,
    completeTwoFactorSignIn: storeCompleteTwoFactorSignIn,
    logout: storeLogout,
    deleteAccount: storeDeleteAccount,
    register: storeRegister,
    refreshAuthToken,
    updateUser: storeUpdateUser,
    clearUser: storeClearUser,
    fetchUser: storeFetchUser,
    clearAllErrors: storeClearAllErrors,
    checkSessionValidity: storeCheckSessionValidity,
    updateLastActivity: storeUpdateLastActivity,
    enableAutoRefresh: storeEnableAutoRefresh,
    disableAutoRefresh: storeDisableAutoRefresh,
    setRefreshThreshold: storeSetRefreshThreshold,
    reset: storeReset,
  } = authStore;

  // Use deferred loading when requested to prevent suspension errors
  const deferredIsLoading = useDeferredValue(authStore.isLoading);
  const effectiveIsLoading = deferred ? deferredIsLoading : authStore.isLoading;

  // =======================================================================
  // AUTHENTICATION ACTIONS
  // =======================================================================

  const login = useCallback(
    (credentials: LoginInput): Promise<LoginOutcome> => storeLogin(credentials),
    [storeLogin]
  );

  const completeTwoFactorSignIn = useCallback(
    (proof: TwoFactorProof): Promise<void> =>
      storeCompleteTwoFactorSignIn(proof),
    [storeCompleteTwoFactorSignIn]
  );

  // Account-scoped caches (the dashboard's among them) clear themselves at
  // the session boundary the store crosses, so nothing is torn down here.
  const logout = useCallback(async (): Promise<void> => {
    await storeLogout();
  }, [storeLogout]);

  const deleteAccount = useCallback(
    async (proof: DeleteAccountRequest): Promise<void> => {
      await storeDeleteAccount(proof);
    },
    [storeDeleteAccount]
  );

  const validateInvitationToken = useCallback(
    async (token: string): Promise<TokenValidationResult> => {
      return await authService.validateInvitationToken(token);
    },
    []
  );

  const register = useCallback(
    async (data: RegisterInput): Promise<void> => {
      await storeRegister(data);
    },
    [storeRegister]
  );

  const waitlist = useCallback(async (data: WaitlistInput): Promise<void> => {
    await authService.waitlist(data);
  }, []);

  const fetchCurrentUser = useCallback(async (): Promise<void> => {
    await storeFetchUser();
  }, [storeFetchUser]);

  const refreshAuthTokenAction = useCallback(async (): Promise<void> => {
    await refreshAuthToken();
  }, [refreshAuthToken]);

  const forgotPassword = useCallback(async (email: string): Promise<void> => {
    await authService.resetPassword(email);
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<void> => {
    await authService.resetPassword(email);
  }, []);

  const confirmPasswordReset = useCallback(
    async (data: {
      email: string;
      token: string;
      password: string;
      password_confirmation: string;
    }): Promise<void> => {
      await authService.confirmPasswordReset(data);
    },
    []
  );

  // =======================================================================
  // USER MANAGEMENT
  // =======================================================================

  const updateUser = useCallback(
    (updates: Partial<User>): void => {
      storeUpdateUser(updates);
    },
    [storeUpdateUser]
  );

  const clearUser = useCallback((): void => {
    storeClearUser();
  }, [storeClearUser]);

  // =======================================================================
  // ERROR MANAGEMENT
  // =======================================================================

  const clearErrors = useCallback((): void => {
    storeClearAllErrors();
  }, [storeClearAllErrors]);

  const clearError = useCallback(
    (type?: 'login' | 'register' | 'refresh' | 'general'): void => {
      if (type === 'login') {
        // Individual error clearing would need to be implemented in the store
        storeClearAllErrors();
      } else if (type === 'register') {
        storeClearAllErrors();
      } else if (type === 'refresh') {
        storeClearAllErrors();
      } else {
        storeClearAllErrors();
      }
    },
    [storeClearAllErrors]
  );

  // =======================================================================
  // SESSION MANAGEMENT
  // =======================================================================

  const checkSessionValidity = useCallback((): boolean => {
    return storeCheckSessionValidity();
  }, [storeCheckSessionValidity]);

  const updateLastActivity = useCallback((): void => {
    storeUpdateLastActivity();
  }, [storeUpdateLastActivity]);

  // =======================================================================
  // AUTO-REFRESH MANAGEMENT
  // =======================================================================

  const enableAutoRefresh = useCallback((): void => {
    storeEnableAutoRefresh();
  }, [storeEnableAutoRefresh]);

  const disableAutoRefresh = useCallback((): void => {
    storeDisableAutoRefresh();
  }, [storeDisableAutoRefresh]);

  const setRefreshThreshold = useCallback(
    (threshold: number): void => {
      storeSetRefreshThreshold(threshold);
    },
    [storeSetRefreshThreshold]
  );

  // =======================================================================
  // STORE MANAGEMENT
  // =======================================================================

  const resetAuthStore = useCallback((): void => {
    storeReset();
  }, [storeReset]);

  // =======================================================================
  // RETURN HOOK INTERFACE
  // =======================================================================

  return {
    // Authentication state
    isAuthenticated,
    user,
    token,
    refreshToken: storeRefreshToken,
    tokenExpiry,

    // Loading states
    isLoading: effectiveIsLoading,
    isLoggingIn,
    isLoggingOut,
    isRegistering,
    isRefreshingToken,

    // Error states
    error,
    loginError,
    registerError,
    refreshError,

    // Authentication actions
    login,
    completeTwoFactorSignIn,
    logout,
    deleteAccount,
    validateInvitationToken,
    register,
    waitlist,
    fetchCurrentUser,
    refreshTokenAction: refreshAuthTokenAction,
    forgotPassword,
    resetPassword,
    confirmPasswordReset,

    // User management
    updateUser,
    clearUser,

    // Error management
    clearErrors,
    clearError,

    // Session management
    checkSessionValidity,
    updateLastActivity,

    // Auto-refresh management
    autoRefreshEnabled: true, // This would come from the store
    enableAutoRefresh,
    disableAutoRefresh,
    setRefreshThreshold,

    // Store management
    resetAuthStore,
  };
};
